import { useCallback, useEffect, useState } from 'react';
import { supabase, type Profile } from '@/lib/supabase';

interface AcademyInfo {
  id: string;
  name: string;
  owner_id: string;
  max_students: number;
}

export default function OwnerManager() {
  const [owners, setOwners] = useState<Profile[]>([]);
  const [academyMap, setAcademyMap] = useState<Record<string, AcademyInfo>>({});
  const [studentCounts, setStudentCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const [ownersRes, academiesRes, studentsRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('role', 'owner').order('created_at', { ascending: false }),
        supabase.from('academies').select('id, name, owner_id, max_students'),
        supabase.from('students').select('academy_id'),
      ]);

      if (ownersRes.error) throw ownersRes.error;
      if (academiesRes.error) throw academiesRes.error;
      if (studentsRes.error) throw studentsRes.error;

      setOwners((ownersRes.data as Profile[]) || []);

      const acMap: Record<string, AcademyInfo> = {};
      ((academiesRes.data as AcademyInfo[]) || []).forEach((a) => {
        acMap[a.owner_id] = a;
      });
      setAcademyMap(acMap);

      const counts: Record<string, number> = {};
      ((studentsRes.data as Array<{ academy_id: string | null }>) || []).forEach((s) => {
        if (s.academy_id) {
          counts[s.academy_id] = (counts[s.academy_id] || 0) + 1;
        }
      });
      setStudentCounts(counts);
    } catch (e) {
      setError('학원장 목록을 불러오지 못했습니다.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const withdraw = async (owner: Profile) => {
    if (
      !window.confirm(
        `'${owner.full_name || '이름 없음'}' 학원장을 강제 탈퇴시키겠습니까?\n학원장 계정이 삭제되며 되돌릴 수 없습니다.`,
      )
    ) {
      return;
    }
    setBusy(owner.id);
    setError('');
    try {
      const { error: err } = await supabase.rpc('delete_account', { p_user_id: owner.id });
      if (err) throw err;
      setOwners((prev) => prev.filter((o) => o.id !== owner.id));
    } catch (e) {
      setError('강제 탈퇴 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setBusy(null);
    }
  };

  const activeCount = owners.filter((o) => o.status === 'approved').length;

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-foreground-950">학원장 목록</h2>
          <p className="text-xs text-foreground-500 mt-1">
            가입한 학원장({owners.length}명 · 승인 {activeCount}명)과 각 학원의 등록 학생 수입니다. 필요 시 학원장을 강제 탈퇴시킬 수 있어요.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-background-200 divide-y divide-background-100">
        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : owners.length === 0 ? (
          <div className="p-8 text-center text-foreground-500 text-sm">
            아직 가입한 학원장이 없습니다.
          </div>
        ) : (
          owners.map((owner) => {
            const isApproved = owner.status === 'approved';
            const academy = academyMap[owner.id];
            const studentCount = academy ? studentCounts[academy.id] || 0 : 0;
            return (
              <div key={owner.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-semibold flex-shrink-0">
                  {(owner.full_name || '?').charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-foreground-950 text-sm">
                      {owner.full_name || '이름 없음'}
                    </span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        isApproved
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-secondary-100 text-secondary-700'
                      }`}
                    >
                      {isApproved ? '승인' : owner.status === 'pending' ? '가입 대기' : owner.status}
                    </span>
                    {owner.phone && (
                      <span className="text-xs text-foreground-500">{owner.phone}</span>
                    )}
                  </div>
                  <div className="text-xs text-foreground-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="text-foreground-700 font-medium">
                      {academy ? academy.name : owner.academy_name || '학원 미등록'}
                    </span>
                    <span>
                      · 이용권{' '}
                      {owner.subscription_plan ? owner.subscription_plan : '미선택'}
                    </span>
                    {owner.subscription_expires_at && (
                      <span>
                        · 만료 {new Date(owner.subscription_expires_at).toLocaleDateString('ko-KR')}
                      </span>
                    )}
                    <span>
                      · 가입 {new Date(owner.created_at).toLocaleDateString('ko-KR')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-primary-700 whitespace-nowrap">
                      학생 {studentCount}
                      {academy ? ` / ${academy.max_students || 100}명` : '명'}
                    </div>
                    <div className="text-xs text-foreground-500">등록 학생</div>
                  </div>
                  <button
                    onClick={() => withdraw(owner)}
                    disabled={busy === owner.id}
                    className="whitespace-nowrap text-red-600 hover:bg-red-50 text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                  >
                    {busy === owner.id ? '처리 중...' : '강제 탈퇴'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}